import { removeCssCharset } from '../gflib/src/lib/css/utils';
import { removeHtmlMetaCharset, replaceOrInsertHtmlMetaCharset } from '../gflib/src/lib/html/charset';
import { handledMediaTypes } from '../gflib/src/lib/html/handledMediaTypes';
import { inlineAtts } from '../gflib/src/lib/html/inlineAtts';
import { rxHead } from '../gflib/src/lib/html/regex';
import { MediaType } from '../gflib/src/lib/media-types';
import { identifyType } from '../gflib/src/lib/mime-atts/identifyType';
import { AttInfoLocalBuffer, AttInfoString } from '../gflib/src/lib/mime-atts/types';
import { attsToStr, isLocalBufAtt } from '../gflib/src/lib/mime-atts/utils';
import { decodeTransferEnc } from '../gflib/src/lib/mime-tree/decodeTransferEnc';
import { getMainHtmlNode } from '../gflib/src/lib/mime-tree/getMainHtmlNode';
import { MediaTypeAndCache } from '../gflib/src/lib/mime-tree/guessNodeMediaType.types';
import { nodeToAtt } from '../gflib/src/lib/mime-tree/nodeToAtt';
import { BodyStrInfo, tryGetBodyStr } from '../gflib/src/lib/mime-tree/tryGetBodyStr';
import { LeafMimeNode, MimeNode } from '../gflib/src/lib/mime-tree/types';
import { pmap } from '../gflib/src/lib/promise/pmap';
import { ReadonlyDeep, StringDecoder, StringEncoder } from '../gflib/src/lib/types';
import { ensureExtOrEquivalent } from '../gflib/src/lib/types-and-exts';
import { bifurcate, isBuf, isStr } from '../gflib/src/lib/utils/common';
import { difference } from '../gflib/src/lib/utils/set';
import { simpleMagic } from '../gflib/src/lib/utils/simpleMagic';

// Clone of gflib's mhtmlToHtml.ts with guessNodeMediaType replaced by a version which does not use gflib's pronom code
// so that we will not explode while using fs-extra.

interface Options {
	verbose?: boolean;
	decode: StringDecoder;
	encode: StringEncoder;
	normalizeToUtf8: boolean;
}

const keepImagesSaneStyle =
	'<style>.nx--mail-html-body img, .nx--scoped img {border:0px; max-width: 100% !important; height: auto !important; max-height: 1000px !important; object-fit: contain !important}</style>';

function getOtherLeafNodes(root: MimeNode, notThis: LeafMimeNode): LeafMimeNode[] {
	const others: LeafMimeNode[] = [];

	const collectOtherNodes = (node: MimeNode): void => {
		if (node.mimeNodeType === 'leaf') {
			if (node !== notThis) {
				others.push(node);
			}
		} else {
			for (const child of node.children) {
				collectOtherNodes(child);
			}
		}
	};
	collectOtherNodes(root);

	return others;
}

function ensureBom(str: string): string {
	// Add BOM? Why not
	if (!str.length || str[0] !== '\uFEFF') {
		str = '\uFEFF' + str;
	}
	return str;
}

export async function mhtmlToHtmlNoPronom(
	root: MimeNode,
	options: Options
): Promise<{
	html: string;
	enc: string | undefined;
	atts: AttInfoLocalBuffer[];
}> {
	const rootHtmlNode = getMainHtmlNode(root);
	const others = getOtherLeafNodes(root, rootHtmlNode);

    // Check for transfer encoding since very simple emails may not have one
    let rootHtmlBodyBuf: Buffer;
    if(root.transferEncoding) {
       rootHtmlBodyBuf = decodeTransferEnc(rootHtmlNode);
    } else {
        rootHtmlBodyBuf = Buffer.from(rootHtmlNode.rawBody);
    }
	const rootHtmlStrInfo = tryGetBodyStr(
		rootHtmlBodyBuf,
		rootHtmlNode.contentTypeCharset,
		options.decode
	);
	if (!isStr(rootHtmlStrInfo.bodyStr)) {
		throw new Error('Expected to get string from root html');
	}

	// Let's remove any in-string charset identification
	const rootHtmlStr = options.normalizeToUtf8
		? ensureBom(replaceOrInsertHtmlMetaCharset(rootHtmlStrInfo.bodyStr, 'utf8').html)
		: rootHtmlStrInfo.bodyStr;

	const allAtts: (AttInfoLocalBuffer | AttInfoString)[] = await pmap(others, async node => {
		const extra = await guessNodeMediaType(node, options.decode, false);

		const [att] = nodeToAtt(node, options.decode, extra);

		if (att.contentType === MediaType.OctetStream) {
			console.warn(`Could not determine media type of node [${att.contentLocation}]`);
		}

		if (att.contentType === MediaType.Svg && !extra.pronomFormat) {
			// May be issue with svgz
			// See:
			// https://github.com/w3c/svgwg/issues/701
			// https://www.w3.org/Graphics/SVG/WG/track/issues/2313
			// https://bugs.chromium.org/p/chromium/issues/detail?id=142251
			// https://bugs.chromium.org/p/chromium/issues/detail?id=106283
			throw new Error("got svg that PRONOM didn't detect");
		}

		// Let's remove any in-string charset identification
		if (options.normalizeToUtf8) {
			// Only HTML and CSS will be string
			if (att.attType === 'string') {
				if (att.contentType === MediaType.Html) {
					att.str = ensureBom(replaceOrInsertHtmlMetaCharset(att.str, 'utf8').html);
					att.toBufEncoding = 'utf8';
				} else if (att.contentType === MediaType.Css) {
					att.str = ensureBom(removeCssCharset(att.str));
					att.toBufEncoding = 'utf8';
				}
			}
		}

		return att;
	});

	const result = inlineAtts(
		rootHtmlStr,
		allAtts,
		rootHtmlNode.contentLocation,
		options.normalizeToUtf8 ? undefined : options.encode,
		options.verbose
	);

	const [notInlinedButExplicitAtt, notInlinedButShouldBe] = bifurcate(result.notInlined, o => {
		return o.contentDisposition?.toLowerCase() === 'attachment';
	});

	if (notInlinedButShouldBe.length) {
		const [maybeImportant, notImportant] = bifurcate(notInlinedButShouldBe, o => {
			// Some mht files we that were "Produced by LateralData.Libraries.Processing.Common.MhtBuilder 1.0.0.2"
			// have these stupid files that aren't used
			if (o.fileName === 'collapsed.png' && o.contentId === '<image-collapsed>') {
				return false;
			}
			if (o.fileName === 'expanded.png' && o.contentId === '<image-expanded>') {
				return false;
			}

			// Sometimes there are some crappy, empty HTML parts that are added, e.g.:
			// <html><head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8"></head><body></body></html>
			// this is a check for these as "not important"
			if (o.contentType === MediaType.Html && o.attType === 'string' && o.str.length < 256) {
				const html = removeHtmlMetaCharset(o.str).toLowerCase().replace(/\s+/g, '');
				if (html.includes('<head></head>') && html.includes('<body></body>')) {
					console.log('Ignoring empty HTML node');
					return false;
				}
			}

			return true;
		});

		if (notImportant.length) {
			console.warn(
				`Did not inline ${notImportant.length} unimportant attachments: ${attsToStr(
					notImportant
				)}`
			);
		}

		if (maybeImportant.length) {
			throw new Error(
				`Did not inline ${
					maybeImportant.length
				} potentially-important attachments: ${attsToStr(maybeImportant)}`
			);
		}
	}

	// At this point, all of the MHTML content nodes haves been accounted for.
	// Still, just because they have all been inlined in the HTML does not mean
	// they are visible. Things like TIFFs, PDFs, Office files, are sometimes
	// inlined, and need to be treated as atts.
	const inlinedAtts = difference(new Set(allAtts), result.notInlined);
	const inlinedButNotVisibleAtts = [...inlinedAtts].filter(isLocalBufAtt).filter(
		o =>
			!handledMediaTypes.has(o.contentType) &&
			// Don't worry about Office themedata.xml and filelist.xml
			o.contentType !== MediaType.Thmx &&
			!(o.contentType === MediaType.Xml && o.fileName === 'filelist.xml') &&
			!(o.contentType === MediaType.Xml && o.fileName === 'colorschememapping.xml')
	);

	const actualAtts = [...notInlinedButExplicitAtt, ...inlinedButNotVisibleAtts];

	// Some of the att media types may have been set or overridden during inlining, e.g. HTML & CSS, so do the ext adding now
	for (const att of actualAtts) {
		att.generatedFileName = ensureExtOrEquivalent(att.contentType, att.generatedFileName);
	}

	const actualBufAtts = actualAtts.map((o): AttInfoLocalBuffer => {
		if (o.attType === 'local-buffer') {
			if (!isBuf(o.buf)) {
				throw new Error('Expected local-buffer att to have a Buffer...');
			}
			return o;
		}

		const { str, ...rest } = o;
		const buf = options.encode(str, o.toBufEncoding || 'latin1');

		return {
			...rest,
			attType: 'local-buffer',
			buf,
			length: buf.length,
		};
	});

	// Insert special styles to prevent super huge images in some email renderings
	if (/nx--(?:mail-html-body|scoped)/.test(result.html)) {
		result.html = result.html.replace(rxHead, match => match + keepImagesSaneStyle);
	}

	const rootHtmlEnc = options.normalizeToUtf8 ? 'utf8' : rootHtmlStrInfo.bodyEnc;

	return {
		html: result.html,
		enc: rootHtmlEnc,
		atts: actualBufAtts,
	};
}


export async function guessNodeMediaType(
	node: ReadonlyDeep<LeafMimeNode>,
	decode: StringDecoder,
	verbose = false
): Promise<MediaTypeAndCache> {
	const bodyBuf = decodeTransferEnc(node);
	let mediaType: string = MediaType.OctetStream;

	let bodyStrInfo: BodyStrInfo | undefined;

	// Try to detect file types using content
	if (mediaType === MediaType.OctetStream) {
		// Check for some common types
		const magicType = simpleMagic(bodyBuf);
		if (magicType) {
			mediaType = magicType;

			if (verbose) {
				console.log(
					`Detected node ${node.treePos} (cid: ${node.contentId}, cloc: ${node.contentLocation}) as ${mediaType} using simple magic`
				);
			}
		} else {
			// Check for HTML
			// Try to figure out what it is...
			bodyStrInfo = tryGetBodyStr(bodyBuf, node.contentTypeCharset, decode);

			if (bodyStrInfo.bodyStr && /^\uFEFF?<!DOCTYPE +html/i.test(bodyStrInfo.bodyStr)) {
				mediaType = MediaType.Html;

				if (verbose) {
					console.log(
						`Detected node ${node.treePos} (cid: ${node.contentId}, cloc: ${node.contentLocation}) as ${mediaType} using HTML magic`
					);
				}
			}
		}
	}

	// Try to detect file types using metadata
	if (mediaType === MediaType.OctetStream) {
		const basicType = identifyType({
			contentType: node.contentType ?? '',
			contentLocation: node.contentLocation,
			fileName: node.contentDispositionFilename || node.contentTypeName,
		});

		// If we determined a type from the basic att info, but content type is generic, set it
		if (basicType && basicType !== mediaType) {
			mediaType = basicType;

			if (verbose) {
				console.log(
					`Detected node ${node.treePos} [${node.contentLocation}] as ${mediaType} using metadata`
				);
			}
		}
	}

	const result: MediaTypeAndCache = {
		bodyBuf,
		mediaType,

		ranPronom: true,

		triedGetBodyStr: !!bodyStrInfo,
		bodyEnc: bodyStrInfo?.bodyEnc,
		bodyStr: bodyStrInfo?.bodyStr,
	};

	return result;
}